import { useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Sparkles, Wand2, ImageOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Background { id: string; name: string; url: string; isDefault: boolean }
interface Overlay { id: string; name: string; url: string; defaultX: number; defaultY: number; defaultScale: number; defaultOpacity: number }
interface CarPhoto {
  id: string;
  slotIndex: number;
  slotKey: string | null;
  stage: 'ORIGINAL' | 'CLEAN' | 'FINAL';
  url: string;
  width?: number | null;
  height?: number | null;
}

export function CompositorDialog({
  open,
  onOpenChange,
  carId,
  photos,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  carId: string;
  photos: CarPhoto[];
}) {
  const qc = useQueryClient();
  const cleanPhotos = useMemo(() => photos.filter((p) => p.stage === 'CLEAN'), [photos]);
  const [photoId, setPhotoId] = useState(cleanPhotos[0]?.id ?? '');
  const [bgId, setBgId] = useState('');
  const [ovId, setOvId] = useState<string>('');
  // Overlay (logo / banner) transform
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [scale, setScale] = useState(1);
  const [opacity, setOpacity] = useState(1);
  const [overlayStretch, setOverlayStretch] = useState(false);
  // Clean-photo (car) placement on the background
  const [carX, setCarX] = useState(0);
  const [carY, setCarY] = useState(0);
  const [carScale, setCarScale] = useState(1);
  const [carOpacity, setCarOpacity] = useState(1);

  // Drag-the-car interaction on the preview canvas
  const previewRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ sx: number; sy: number; cx: number; cy: number } | null>(null);
  function onPreviewPointerDown(e: React.PointerEvent) {
    if (!photoId) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { sx: e.clientX, sy: e.clientY, cx: carX, cy: carY };
  }
  function onPreviewPointerMove(e: React.PointerEvent) {
    if (!dragRef.current || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    // Map drag distance from preview pixels → canvas pixels using the true
    // canvas dimensions (which the server also uses).
    const dx = ((e.clientX - dragRef.current.sx) / rect.width)  * canvasW;
    const dy = ((e.clientY - dragRef.current.sy) / rect.height) * canvasH;
    const maxX = canvasW;
    const maxY = canvasH;
    setCarX(Math.max(-maxX, Math.min(maxX, dragRef.current.cx + dx)));
    setCarY(Math.max(-maxY, Math.min(maxY, dragRef.current.cy + dy)));
  }
  function onPreviewPointerUp(e: React.PointerEvent) {
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    dragRef.current = null;
  }

  const backgrounds = useQuery({ queryKey: ['backgrounds'], queryFn: () => api.get<Background[]>('/backgrounds'), enabled: open });
  const overlays = useQuery({ queryKey: ['overlays'], queryFn: () => api.get<Overlay[]>('/overlays'), enabled: open });

  // Auto-select a default background
  if (open && !bgId && backgrounds.data && backgrounds.data.length > 0) {
    const def = backgrounds.data.find((b) => b.isDefault) ?? backgrounds.data[0];
    if (def) setBgId(def.id);
  }
  if (open && !photoId && cleanPhotos.length > 0 && cleanPhotos[0]) setPhotoId(cleanPhotos[0].id);

  const bg = backgrounds.data?.find((b) => b.id === bgId);
  const ov = overlays.data?.find((o) => o.id === ovId);
  const clean = cleanPhotos.find((p) => p.id === photoId);

  // Use the actual clean-photo dimensions so the preview math matches the
  // server's compositor (which uses cleanMeta.width as the canvas size).
  // Without this the preview lies — drag the car 10% across in preview and
  // it ends up somewhere else in the rendered final.
  const canvasW = clean?.width  ?? 1920;
  const canvasH = clean?.height ?? 1080;

  const applyOne = useMutation({
    mutationFn: async (targetPhotoId: string) => {
      const body: Record<string, unknown> = { backgroundId: bgId };
      // Always send car placement so the back end honors the user's framing.
      body.cleanTransform = { x: carX, y: carY, scale: carScale, opacity: carOpacity };
      if (ovId) {
        body.overlayId = ovId;
        body.overlayTransform = overlayStretch
          ? { x: 0, y: 0, scale: 1, opacity, stretch: true }
          : { x, y, scale, opacity, stretch: false };
      }
      return api.post<{ url: string }>(`/photos/${targetPhotoId}/composite`, body);
    },
  });

  async function applyToThis() {
    if (!photoId) return;
    try {
      await applyOne.mutateAsync(photoId);
      toast.success('Applied');
      qc.invalidateQueries({ queryKey: ['car', carId] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Composite failed');
    }
  }

  async function applyToAll() {
    if (cleanPhotos.length === 0) return;
    try {
      for (const p of cleanPhotos) {
        // serial to keep server load light
        await applyOne.mutateAsync(p.id);
      }
      toast.success(`Applied to ${cleanPhotos.length} photos`);
      qc.invalidateQueries({ queryKey: ['car', carId] });
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Composite failed');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="h-4 w-4" /> Apply background & overlay</DialogTitle>
          <DialogDescription>
            Pick a background (and optional logo overlay), preview, then apply to one shot or the whole car.
          </DialogDescription>
        </DialogHeader>

        {cleanPhotos.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
            <ImageOff className="h-8 w-8" />
            No cutouts yet. Capture photos with auto-cutout enabled, or re-run cutout from the car detail.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-[220px_1fr]">
            {/* Preview + photo picker */}
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Preview photo</Label>
              <div className="grid grid-cols-3 gap-1 md:grid-cols-1">
                {cleanPhotos.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPhotoId(p.id)}
                    className={cn(
                      'relative overflow-hidden rounded border-2 transition-colors',
                      p.id === photoId ? 'border-primary' : 'border-transparent hover:border-white/20',
                    )}
                  >
                    <img src={p.url} alt={p.slotKey ?? ''} className="aspect-[4/3] w-full object-cover bg-secondary" />
                  </button>
                ))}
              </div>
            </div>

            {/* Main column: live preview + pickers */}
            <div className="space-y-4">
              <div
                ref={previewRef}
                className="relative aspect-[4/3] touch-none cursor-grab overflow-hidden rounded-md border border-border bg-muted active:cursor-grabbing"
                onPointerDown={onPreviewPointerDown}
                onPointerMove={onPreviewPointerMove}
                onPointerUp={onPreviewPointerUp}
                onPointerCancel={onPreviewPointerUp}
              >
                {bg && <img src={bg.url} alt="" draggable={false} className="absolute inset-0 h-full w-full object-cover select-none" />}
                {clean && (
                  <img
                    src={clean.url}
                    alt=""
                    draggable={false}
                    className="select-none"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      objectFit: 'contain',
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                      // px offsets in canvas coordinate space → percent of preview.
                      // Uses the actual canvas size so preview and final match.
                      transform: `translate(${(carX / canvasW) * 100}%, ${(carY / canvasH) * 100}%) scale(${carScale})`,
                      opacity: carOpacity,
                      transition: dragRef.current ? 'none' : 'transform 90ms ease-out, opacity 90ms',
                    }}
                  />
                )}
                {ov && (
                  overlayStretch ? (
                    <img
                      src={ov.url}
                      alt=""
                      draggable={false}
                      className="absolute inset-0 h-full w-full select-none"
                      style={{
                        objectFit: 'fill',
                        pointerEvents: 'none',
                        opacity,
                      }}
                    />
                  ) : (
                    <img
                      src={ov.url}
                      alt=""
                      draggable={false}
                      className="select-none"
                      style={{
                        position: 'absolute',
                        pointerEvents: 'none',
                        left: `${(x / canvasW) * 100}%`,
                        top: `${(y / canvasH) * 100}%`,
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                        opacity,
                        maxWidth: '60%',
                      }}
                    />
                  )
                )}
                <div className="pointer-events-none absolute bottom-1.5 left-1/2 -translate-x-1/2 rounded-full bg-black/45 px-2 py-0.5 text-[10px] text-white/80 backdrop-blur">
                  Drag to position · use sliders for fine control
                </div>
              </div>

              {/* Car placement — sliders for fine control (work everywhere
                  including mobile) plus drag-the-preview for the natural way. */}
              <div className="rounded-md border border-border bg-card/60 p-3">
                <div className="mb-3 flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Car placement</Label>
                  <button
                    type="button"
                    className="text-[11px] text-muted-foreground hover:text-foreground"
                    onClick={() => { setCarX(0); setCarY(0); setCarScale(1); setCarOpacity(1); }}
                  >
                    Reset
                  </button>
                </div>
                <div className="space-y-3">
                  <SliderRow label="Horizontal" v={carX} onChange={setCarX} min={-canvasW} max={canvasW} step={Math.max(1, Math.round(canvasW / 200))} fmt={(v) => `${v > 0 ? '+' : ''}${Math.round(v)}px`} />
                  <SliderRow label="Vertical"   v={carY} onChange={setCarY} min={-canvasH} max={canvasH} step={Math.max(1, Math.round(canvasH / 200))} fmt={(v) => `${v > 0 ? '+' : ''}${Math.round(v)}px`} />
                  <SliderRow label="Scale"      v={carScale}   onChange={setCarScale}   min={0.2} max={3}  step={0.02} fmt={(v) => `${v.toFixed(2)}×`} />
                  <SliderRow label="Opacity"    v={carOpacity} onChange={setCarOpacity} min={0}   max={1}  step={0.02} fmt={(v) => `${Math.round(v * 100)}%`} />
                </div>
              </div>

              <Tabs defaultValue="bg" className="space-y-3">
                <TabsList>
                  <TabsTrigger value="bg">Background</TabsTrigger>
                  <TabsTrigger value="ov">Overlay</TabsTrigger>
                </TabsList>
                <TabsContent value="bg">
                  {!backgrounds.data?.length ? (
                    <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                      No backgrounds uploaded yet. Go to Backgrounds to add one.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 md:grid-cols-4">
                      {backgrounds.data.map((b) => (
                        <button
                          key={b.id}
                          onClick={() => setBgId(b.id)}
                          className={cn(
                            'relative overflow-hidden rounded border-2 transition-colors',
                            b.id === bgId ? 'border-primary' : 'border-transparent hover:border-white/30',
                          )}
                          title={b.name}
                        >
                          <img src={b.url} alt="" className="aspect-[4/3] w-full object-cover" />
                          <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-[10px] text-white truncate">{b.name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="ov" className="space-y-3">
                  <div className="grid grid-cols-3 gap-2 md:grid-cols-4">
                    <button
                      onClick={() => setOvId('')}
                      className={cn(
                        'flex aspect-[4/3] items-center justify-center rounded border-2 text-xs text-muted-foreground transition-colors',
                        ovId === '' ? 'border-primary bg-primary/10' : 'border-dashed border-border hover:border-white/30',
                      )}
                    >
                      No overlay
                    </button>
                    {overlays.data?.map((o) => (
                      <button
                        key={o.id}
                        onClick={() => {
                          setOvId(o.id);
                          setX(o.defaultX); setY(o.defaultY); setScale(o.defaultScale); setOpacity(o.defaultOpacity);
                          setOverlayStretch(false);
                        }}
                        className={cn(
                          'relative overflow-hidden rounded border-2 bg-[linear-gradient(135deg,#1a2230_25%,transparent_25%,transparent_50%,#1a2230_50%,#1a2230_75%,transparent_75%)] bg-[length:8px_8px] transition-colors',
                          o.id === ovId ? 'border-primary' : 'border-transparent hover:border-white/30',
                        )}
                        title={o.name}
                      >
                        <img src={o.url} alt="" className="aspect-[4/3] w-full object-contain p-2" />
                        <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-[10px] text-white truncate">{o.name}</div>
                      </button>
                    ))}
                  </div>
                  {ov && (
                    <div className="rounded-md border border-border bg-card/60 p-3">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                          Overlay placement
                        </Label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setOverlayStretch((v) => !v)}
                            className={cn(
                              'rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
                              overlayStretch
                                ? 'border-primary bg-primary/15 text-primary'
                                : 'border-border text-muted-foreground hover:text-foreground',
                            )}
                            title="Resize overlay to cover the entire image"
                          >
                            {overlayStretch ? '✓ Covering image' : 'Stretch to fill'}
                          </button>
                          <button
                            type="button"
                            className="text-[11px] text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setOverlayStretch(false);
                              setX(ov.defaultX); setY(ov.defaultY);
                              setScale(ov.defaultScale); setOpacity(ov.defaultOpacity);
                            }}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                      <div className={cn('space-y-3', overlayStretch && 'pointer-events-none opacity-50')}>
                        <SliderRow label="Horizontal" v={x} onChange={setX} min={0} max={canvasW} step={Math.max(1, Math.round(canvasW / 200))} fmt={(v) => `${Math.round(v)}px`} />
                        <SliderRow label="Vertical"   v={y} onChange={setY} min={0} max={canvasH} step={Math.max(1, Math.round(canvasH / 200))} fmt={(v) => `${Math.round(v)}px`} />
                        <SliderRow label="Scale"      v={scale}   onChange={setScale}   min={0.1} max={5} step={0.02} fmt={(v) => `${v.toFixed(2)}×`} />
                      </div>
                      {/* Opacity always available, even in stretch mode (lets you tint the whole frame). */}
                      <div className="mt-3">
                        <SliderRow label="Opacity" v={opacity} onChange={setOpacity} min={0} max={1} step={0.02} fmt={(v) => `${Math.round(v * 100)}%`} />
                      </div>
                      {overlayStretch && (
                        <p className="mt-2 text-[11px] text-muted-foreground">
                          Overlay is sized to the full canvas. Adjust Opacity to tone it down.
                        </p>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <DialogClose asChild><Button variant="ghost">Close</Button></DialogClose>
          <Button variant="outline" disabled={!bgId || !photoId || applyOne.isPending} onClick={applyToThis}>
            {applyOne.isPending ? 'Applying…' : 'Apply to this photo'}
          </Button>
          <Button disabled={!bgId || cleanPhotos.length === 0 || applyOne.isPending} onClick={applyToAll} className="gap-2">
            <Sparkles className="h-4 w-4" />
            {applyOne.isPending ? 'Applying…' : `Apply to all ${cleanPhotos.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NumField({ label, v, onChange, step, min, max }: {
  label: string; v: number; onChange: (v: number) => void; step: number; min?: number; max?: number;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type="number" value={v} step={step} min={min} max={max} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

/** Slider row used by the car-placement panel — touch-friendly, shows a
 *  formatted readout on the right so the value is always visible. */
function SliderRow({ label, v, onChange, min, max, step, fmt }: {
  label: string;
  v: number;
  onChange: (v: number) => void;
  min: number; max: number; step: number;
  fmt: (v: number) => string;
}) {
  return (
    <div className="flex items-center gap-3">
      <Label className="w-20 shrink-0 text-xs text-muted-foreground">{label}</Label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={v}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-2 flex-1 appearance-none rounded-full bg-secondary accent-cyan-400"
      />
      <span className="w-14 shrink-0 text-right text-[11px] tabular-nums text-foreground/80">
        {fmt(v)}
      </span>
    </div>
  );
}
